import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Pin } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { GoogleMap } from "@/components/GoogleMap";
import { trackPhoneClick } from "@/components/ConversionTracking";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useI18n();

  return (
    <footer className="bg-white border-t border-border/10">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Company Info */}
          <div>
            <h3 className="font-playfair text-lg font-bold text-primary mb-4">Aureum VII Trading</h3>
            <p className="font-inter text-sm text-muted-foreground mb-4 leading-relaxed">
              {t.footer.description}
            </p>
            <div className="flex gap-3">
              <a
                href="https://twitter.com/AureumVII"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-white transition-colors"
                aria-label="Follow us on Twitter"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61582702018023"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-white transition-colors"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/aureum7trading/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-white transition-colors"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.linkedin.com/in/aureum-vii-541511390/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-white transition-colors"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://pin.it/6XkCS9rTC"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-white transition-colors"
                aria-label="Follow us on Pinterest"
              >
                <Pin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links - Updated navigation structure */}
          <div>
            <h3 className="font-playfair text-lg font-bold text-primary mb-4">{t.footer.quickLinks}</h3>
            <ul className="space-y-2 font-inter text-sm">
              <li>
                <LocalizedLink to="/" className="text-muted-foreground hover:text-primary transition-colors">
                  Home
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/smart-toilets" className="text-muted-foreground hover:text-primary transition-colors">
                  Products
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/collection" className="text-muted-foreground hover:text-primary transition-colors">
                  Collection
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/trade-brochure" className="text-muted-foreground hover:text-primary transition-colors">
                  Trade Brochure
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/compare" className="text-muted-foreground hover:text-primary transition-colors">
                  Compare Models
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/smart-toilet-cost" className="text-muted-foreground hover:text-primary transition-colors">
                  Smart Toilet Cost Guide
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/technology" className="text-muted-foreground hover:text-primary transition-colors">
                  Technology
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/business" className="text-muted-foreground hover:text-primary transition-colors">
                  B2B & Projects
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/sustainability" className="text-muted-foreground hover:text-primary transition-colors">
                  Sustainability
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/about" className="text-muted-foreground hover:text-primary transition-colors">
                  About
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/faq" className="text-muted-foreground hover:text-primary transition-colors">
                  FAQ
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
                  Contact
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
                  Blog
                </LocalizedLink>
              </li>
            </ul>
          </div>


          {/* Products - Updated to REM7 hierarchy */}
          <div>
            <h3 className="font-playfair text-lg font-bold text-primary mb-4">{t.footer.products}</h3>
            <ul className="space-y-2 font-inter text-sm">
              <li>
                <LocalizedLink to="/products/rem7" className="text-muted-foreground hover:text-primary transition-colors">
                  REM7 - Flagship
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/products/luxa" className="text-muted-foreground hover:text-primary transition-colors">
                  Luxa - Luxury Premium
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/products/aria" className="text-muted-foreground hover:text-primary transition-colors">
                  Aria - Design Variant
                </LocalizedLink>
              </li>
              <li className="pt-2 border-t border-border/10 mt-2">
                <span className="text-xs text-muted-foreground/70 uppercase tracking-wider">REM7 Variants</span>
              </li>
              <li>
                <LocalizedLink to="/products/m3" className="text-muted-foreground hover:text-primary transition-colors">
                  REM7 M3
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/products/s1" className="text-muted-foreground hover:text-primary transition-colors">
                  REM7 S1
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/products/k2" className="text-muted-foreground hover:text-primary transition-colors">
                  REM7 K2
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/products/u1" className="text-muted-foreground hover:text-primary transition-colors">
                  REM7 U1
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* Markets / Where We Ship */}
          <div>
            <h3 className="font-playfair text-lg font-bold text-primary mb-4">Where We Ship</h3>
            <ul className="space-y-2 font-inter text-sm">
              <li>
                <LocalizedLink to="/european-markets" className="text-muted-foreground hover:text-primary transition-colors">
                  European Markets
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/markets/spain" className="text-muted-foreground hover:text-primary transition-colors">
                  Spain
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/markets/germany" className="text-muted-foreground hover:text-primary transition-colors">
                  Germany
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/markets/france" className="text-muted-foreground hover:text-primary transition-colors">
                  France
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/markets/italy" className="text-muted-foreground hover:text-primary transition-colors">
                  Italy
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/markets/uk" className="text-muted-foreground hover:text-primary transition-colors">
                  United Kingdom
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink to="/markets/usa" className="text-muted-foreground hover:text-primary transition-colors">
                  USA (B2B, 50+ units)
                </LocalizedLink>
              </li>
            </ul>
          </div>


          {/* Contact Info */}
          <div>
            <h3 className="font-playfair text-lg font-bold text-primary mb-4">{t.contact.title}</h3>
            <ul className="space-y-3 font-inter text-sm">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" />
                <div>
                  <a 
                    href="mailto:info@aureum7trading.com" 
                    className="text-accent hover:text-accent/80 transition-colors font-semibold block"
                  >
                    info@aureum7trading.com
                  </a>
                  <span className="text-xs text-muted-foreground">Response within 24 hours</span>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" />
                <div>
                  <a 
                    href="tel:+34642278939"
                    onClick={trackPhoneClick}
                    className="text-muted-foreground hover:text-primary transition-colors font-medium block"
                  >
                    +34 642 278 939
                  </a>
                  <span className="text-xs text-muted-foreground">{t.contact.weekdays}</span>
                </div>
              </li>
              <li className="flex items-start gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" />
                <div>
                  <span>Marbella, Andalusia, Spain</span>
                  <span className="text-xs block">European Headquarters</span>
                </div>
              </li>
            </ul>
            
            <div className="mt-6">
              <h4 className="font-playfair text-sm font-semibold text-primary mb-2">{t.footer.support}</h4>
              <ul className="space-y-2 font-inter text-sm">
                <li>
                  <LocalizedLink to="/blog/luxury-smart-toilet-guide" className="text-muted-foreground hover:text-primary transition-colors">
                    Smart Toilet Buying Guide
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/blog/smart-toilet-vs-bidet" className="text-muted-foreground hover:text-primary transition-colors">
                    Smart Toilet vs Bidet
                  </LocalizedLink>
                </li>
                <li>
                  <LocalizedLink to="/agent-login" className="text-accent hover:text-accent/80 transition-colors font-semibold">
                    Agent Portal — Sign in
                  </LocalizedLink>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-12 pt-12 border-t border-border/10">
          <h3 className="font-playfair text-xl font-bold text-primary text-center mb-6">{t.contact.ourLocation}</h3>
          <GoogleMap height="300px" className="max-w-4xl mx-auto" />
        </div>

        <Separator className="my-8" />

        {/* Business Identity Disclaimer */}
        <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border/10">
          <p className="font-inter text-xs text-muted-foreground text-center leading-relaxed">
            <strong>Important Notice:</strong> Aureum VII Trading (AVT) is a specialized B2B supplier and white-label partner for luxury smart bathroom technology. 
            REM7 is our flagship product line of premium smart toilet systems. 
            We are <strong>not affiliated</strong> with any financial trading, forex brokerage, cryptocurrency, or investment firms. 
            Our business is exclusively focused on premium smart toilet systems and intelligent bathroom solutions for the European market.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <p className="font-inter text-center sm:text-left">
            © {currentYear} Aureum VII Trading. {t.footer.allRightsReserved}
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 font-inter">
            <LocalizedLink to="/privacy-policy" className="hover:text-primary transition-colors">
              {t.footer.privacyPolicy}
            </LocalizedLink>
            <LocalizedLink to="/return-policy" className="hover:text-primary transition-colors">
              {t.footer.returnPolicy}
            </LocalizedLink>
            <LocalizedLink to="/terms-conditions" className="hover:text-primary transition-colors">
              {t.footer.termsConditions}
            </LocalizedLink>
            <LocalizedLink to="/shipping-policy" className="hover:text-primary transition-colors">
              {t.footer.shippingPolicy}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </footer>
  );
};
